import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getDepartmentList,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from "../api/department.api";
import type {
  DepartmentPayload,
  GetDepartmentListParams,
} from "@/interface/masterManagement";
import { departmentKeys, STALE_5_MIN } from "@/lib/queryKey";
import { getNextPage, isPaginated } from "@/utils/helper";

export const useDepartmentList = (params: GetDepartmentListParams) =>
  useQuery({
    queryKey: departmentKeys.list(params),
    queryFn: () => getDepartmentList(params),
    ...STALE_5_MIN,
  });

export const useInfiniteDepartmentList = (params?: GetDepartmentListParams) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: departmentKeys.list({ ...params, type: "infinite" }),
    queryFn: ({ pageParam }) =>
      getDepartmentList(
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    ...STALE_5_MIN,
  });
};

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDepartment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    },
  });
};

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: DepartmentPayload;
    }) => updateDepartment(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    },
  });
};

export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteDepartment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    },
  });
};
